import { Pencil, Plus, RotateCcw, Trash } from "lucide-react";

export type AddressCardRow = {
	label: string;
	value: string;
};

export type AddressCardItem = {
	key: string | number;
	title: string;
	rows: AddressCardRow[];
	isActive?: boolean;
	onEdit?: () => void;
	onToggleActive?: () => void;
	toggleActiveLabel?: string;
	toggleInactiveLabel?: string;
	onDelete?: () => void;
};

export function AddressCardsBoard({
	title,
	subtitle,
	addLabel,
	onAdd,
	cards,
	actionsDisabled = false,
}: {
	title: string;
	subtitle: string;
	addLabel: string;
	onAdd: () => void;
	cards: AddressCardItem[];
	actionsDisabled?: boolean;
}) {
	return (
		<div className='animate-in fade-in slide-in-from-bottom-4 duration-500'>
			<div className='mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between'>
				<div>
					<h2 className='text-2xl font-semibold text-gray-800'>{title}</h2>
					<p className='text-sm text-gray-500'>{subtitle}</p>
				</div>
			</div>

			<div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'>
				<button
					type='button'
					onClick={onAdd}
					className='group cursor-pointer flex min-h-45 flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 text-center text-slate-500 transition hover:border-primary hover:text-primary'
				>
					<span className='flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm'>
						<Plus className='h-5 w-5' />
					</span>
					<span className='text-sm font-semibold'>{addLabel}</span>
				</button>

				{cards.map((card) => {
					const isActive = card.isActive !== false;
					const statusClass = isActive
						? "border-emerald-200 bg-emerald-50 text-emerald-700"
						: "border-slate-300 bg-slate-100 text-slate-600";

					return (
					<div
						key={card.key}
						className={`rounded-2xl border bg-white p-4 shadow-sm ${
							isActive ? "border-slate-200" : "border-slate-300"
						}`}
					>
						<div className='mb-3 flex items-start justify-between gap-2'>
							<div className='text-sm font-semibold text-gray-700'>
								{card.title}
							</div>
							<span
								className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass}`}
							>
								{isActive ? "Activado" : "Desactivado"}
							</span>
						</div>
						<div className='space-y-1 text-sm text-gray-500'>
							{card.rows.map((row) => (
								<div
									key={`${card.key}-${row.label}`}
									className='flex justify-left gap-2'
								>
									<span className='text-gray-400'>{row.label}</span>
									<span className='text-gray-600'>
										{row.value || "-"}
									</span>
								</div>
							))}
						</div>
						<div className='mt-4 flex items-center justify-end gap-2'>
							{card.onEdit ? (
								<button
									type='button'
									aria-label='Editar dirección'
									className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white disabled:cursor-not-allowed disabled:opacity-60'
									disabled={actionsDisabled}
									onClick={card.onEdit}
								>
									<Pencil className='h-4 w-4' />
								</button>
							) : null}
							{card.onToggleActive ? (
								<button
									type='button'
									aria-label={isActive ? "Desactivar dirección" : "Activar dirección"}
									className={`cursor-pointer inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
										isActive
											? "bg-error-text hover:bg-red-700"
											: "bg-emerald-600 hover:bg-emerald-700"
									}`}
									disabled={actionsDisabled}
									onClick={card.onToggleActive}
								>
									{isActive ? (
										<Trash className='h-3.5 w-3.5' />
									) : (
										<RotateCcw className='h-3.5 w-3.5' />
									)}
									<span>
										{isActive
											? (card.toggleActiveLabel ?? "Desactivar")
											: (card.toggleInactiveLabel ?? "Activar")}
									</span>
								</button>
							) : null}
							{card.onDelete ? (
								<button
									type='button'
									aria-label='Eliminar dirección'
									className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-error-text text-white disabled:cursor-not-allowed disabled:opacity-60'
									disabled={actionsDisabled}
									onClick={card.onDelete}
								>
									<Trash className='h-4 w-4' />
								</button>
							) : null}
						</div>
					</div>
					);
				})}
			</div>
		</div>
	);
}
